/**
 * Temporary canonical authority for the live PUBLIC reservation background.
 * Independent of Theme Studio draft/publish/activate state.
 *
 * Canonical setting: reservation_settings/{unitId}.publicBackgroundImageUrl
 * Canonical asset:   units/{unitId}/ui_themes/booking_backgrounds/bg_<uuid>.<ext>
 *
 * An empty string explicitly disables the image; undefined/null is reserved
 * for previously stored legacy theme.backgroundImageUrl fallback.
 */
export const PUBLIC_BOOKING_BACKGROUND_STORAGE_BUCKET = 'mintleaf-74d27.firebasestorage.app';
export const PUBLIC_BOOKING_BACKGROUND_MAX_BYTES = 5 * 1024 * 1024;
export const PUBLIC_BOOKING_BACKGROUND_ACCEPT = 'image/jpeg,image/png,image/webp';

const MIME_EXTENSIONS: Record<string, 'jpg' | 'png' | 'webp'> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export const publicBookingBackgroundExtension = (mimeType: string): 'jpg' | 'png' | 'webp' | null =>
  MIME_EXTENSIONS[mimeType] ?? null;

export const publicBookingBackgroundStoragePath = (
  unitId: string,
  uuid: string,
  extension: 'jpg' | 'png' | 'webp',
): string =>
  `units/${unitId}/ui_themes/booking_backgrounds/bg_${uuid}.${extension}`;

const FILE_NAME = /^bg_[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.(?:jpg|png|webp)$/i;

/**
 * Restrict new public booking background writes to download URLs for an image
 * in THIS unit's dedicated folder in the configured Firebase Storage bucket.
 * This is checked by the canonical authenticated settings-save backend too.
 */
export const isCanonicalPublicBookingBackgroundUrl = (value: unknown, unitId: string): value is string => {
  if (typeof value !== 'string' || value.length === 0 || value.length > 2048) return false;
  if (!unitId || unitId.includes('/') || unitId.includes('\\')) return false;

  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'firebasestorage.googleapis.com'
      || url.username || url.password || url.port || url.hash) return false;

    const prefix = `/v0/b/${PUBLIC_BOOKING_BACKGROUND_STORAGE_BUCKET}/o/`;
    if (!url.pathname.startsWith(prefix)) return false;
    const objectPath = decodeURIComponent(url.pathname.slice(prefix.length));
    const expectedPrefix = `units/${unitId}/ui_themes/booking_backgrounds/`;
    if (!objectPath.startsWith(expectedPrefix) || !FILE_NAME.test(objectPath.slice(expectedPrefix.length))) return false;
    const queryKeys: string[] = [];
    url.searchParams.forEach((_value, key) => { queryKeys.push(key); });
    if (queryKeys.length !== 2 || !queryKeys.includes('alt') || !queryKeys.includes('token')
      || url.searchParams.get('alt') !== 'media'
      || !/^[A-Za-z0-9._~-]{1,256}$/.test(url.searchParams.get('token') ?? '')) return false;
    return true;
  } catch {
    return false;
  }
};

/** Canonical setting wins; explicit '' does not resurrect an older legacy image. */
export const resolvePublicBookingBackgroundUrl = (
  canonical: string | null | undefined,
  legacy: string | null | undefined,
): string | undefined =>
  canonical === undefined || canonical === null ? (legacy || undefined) : (canonical || undefined);
